import { createApi } from "@reduxjs/toolkit/query/react";

import { TXeroInvoice } from "@/interfaces/invoicing.interface";
import { InvoiceItem, TrackingItem } from "@/interfaces/xero.interface";
import { env } from "@/lib/env";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const xeroApi = createApi({
  reducerPath: "xeroApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["xero", "tracking"],
  endpoints: (builder) => ({
    // auth for xero
    xeroAuth: builder.query<{ redirectUrl: string }, { redirectUrl: string }>({
      query: ({ redirectUrl }) => {
        return `${env.BACKEND_URL}/xero/workspace/${getWorkspace()}/connect?redirectUrl=${redirectUrl}`;
      },
      providesTags: ["xero"],
    }),

    xeroAuthDisconnect: builder.query({
      query: () => ({
        url: `xero/workspace/${getWorkspace()}/disconnect`,
      }),
      providesTags: ["xero"],
    }),

    //get invoice items
    xeroInvoiceItems: builder.query<
      GeneralResponse<InvoiceItem[]>,
      { filter: string }
    >({
      query: ({ filter }) => ({
        url: `xero/workspace/${getWorkspace()}/items/${filter}`,
      }),
      providesTags: ["xero"],
    }),
    //sync invoice items
    xeroInvoiceItemsSync: builder.mutation<string, string>({
      query: () => ({
        url: `xero/workspace/${getWorkspace()}/items/sync`,
        method: "PATCH",
      }),
      invalidatesTags: ["xero"],
    }),
    //get invoiced dockets
    xeroInvoices: builder.query<
      GeneralResponse<TXeroInvoice>,
      { filter: string }
    >({
      query: ({ filter }) => ({
        url: `/workspace/${getWorkspace()}/invoice/xero?${filter ?? ""}`,
      }),
      providesTags: ["xero"],
    }),
    //allocate xero invoice
    allocateXeroInvoice: builder.mutation<
      object,
      { invoiceId: string; data: { customerId: string; projectId: string } }
    >({
      query: ({ invoiceId, data }) => ({
        url: `workspace/${getWorkspace()}/invoice/xero/${invoiceId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["xero"],
    }),

    //get xero tracking items
    xeroTrackingItems: builder.query<TrackingItem[], { filter: string }>({
      query: ({ filter }) => ({
        url: `xero/workspace/${getWorkspace()}/tracking/${filter}`,
      }),
      transformResponse: (response: APIResponse<TrackingItem[]>) =>
        response?.data,
      providesTags: ["tracking"],
    }),

    //Manually Sync Xero Invoices
    syncXeroInvoices: builder.mutation<object, string>({
      query: () => ({
        url: `xero/workspace/${getWorkspace()}/invoice/sync`,
        method: "PATCH",
      }),
      invalidatesTags: ["xero"],
    }),
  }),
});

export const {
  useXeroAuthQuery,
  useLazyXeroAuthQuery,
  useLazyXeroAuthDisconnectQuery,
  useXeroInvoiceItemsQuery,
  useXeroInvoiceItemsSyncMutation,
  useXeroInvoicesQuery,
  useXeroTrackingItemsQuery,
  useLazyXeroTrackingItemsQuery,
  useAllocateXeroInvoiceMutation,
  useSyncXeroInvoicesMutation,
} = xeroApi;
